import { api, LightningElement, wire } from "lwc";
import classNames from "classnames";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { MenuAdapter, MenuAdapterValue } from "common/menuAdapter";
import { BooleanAttr, attrToBool } from "common/helpers";

import * as locales from "./locales";
import { Action, ActionSection } from "./types";
export type { Action, ActionSection };

type ActionWithHandler = Action & { handler: () => void };

export enum ActionButtons {
  PRIMARY = "PRIMARY",
  MORE_ACTIONS = "MORE_ACTIONS",
}

export default class extends LightningElement {
  @api action: Action | Array<Action> | null = null;
  @api actions: ActionSection[] = [];
  @api contentType = "";

  @api
  setFocusOnButton(actionType: ActionButtons, name?: string) {
    let buttonForFocus = null;
    if (actionType === ActionButtons.PRIMARY) {
      const buttonName = name ? name : this.primaryActions[0].name;
      buttonForFocus = this.template.querySelector(
        `.buttons > [data-key='${buttonName}']`
      );
    } else if (actionType === ActionButtons.MORE_ACTIONS) {
      buttonForFocus = this.template.querySelector(
        '.buttons > div > button[data-key="more-dropdown"]'
      );
    }
    buttonForFocus?.focus();
  }

  @api
  get open() {
    return this.menu.state.open;
  }
  set open(value: BooleanAttr) {
    attrToBool(value) ? this.menu.open() : this.menu.close();
  }

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;

  private get actionsFlattened() {
    return this.actions.flatMap((s) => s.actions);
  }

  private get hasActions() {
    return this.actionsFlattened.length > 0;
  }

  @api
  focus() {
    (
      this.template.querySelector(".buttons")?.firstChild as HTMLElement
    )?.focus();
  }

  @wire(MenuAdapter, { items: "$actionsFlattened", template: "$template" })
  private menu: MenuAdapterValue = null!;

  private get shouldRenderDropdown() {
    return this.contentType !== "learningpath";
  }

  private get primaryActions() {
    const actions = [this.action].flat().filter(Boolean) as Array<Action>;
    return actions.map((action) => ({
      ...action,
      renderButtonIcon: action.icon && !action.showLabel,
      iconSymbol: action.showLabel ? action.icon?.symbol : undefined,
      iconSprite: action.showLabel ? action.icon?.sprite : undefined,
      className: classNames(
        "action--primary",
        action.disabled && "action--primary-disabled"
      ),
    }));
  }

  private get shouldRenderActionsMenu() {
    const hasPrimaryActions = this.primaryActions.length > 0;
    const hasDropdownAvailable = this.shouldRenderDropdown && this.hasActions;

    return hasPrimaryActions || hasDropdownAvailable;
  }

  private get sections(): (ActionSection & {
    key: string;
    actions: ActionWithHandler[];
  })[] {
    return this.actions.map((section, index) => {
      return {
        ...section,
        key: `section-${index}`,
        actions: section.actions.map((action) => ({
          ...action,
          handler: () => {
            this.dispatchEvent(new CustomEvent("action", { detail: action }));
          },
        })),
      };
    });
  }

  private get triggerClassName() {
    return classNames("trigger button button--bare button--small", {
      "trigger--action": this.action,
      "trigger--hide": !this.hasActions,
    });
  }

  private handlePrimaryAction() {
    this.setFocusOnButton(ActionButtons.PRIMARY);
    this.dispatchEvent(new CustomEvent("action", { detail: this.action! }));
  }
}
